import type { AppDocsQuery, MyTourFieldsFragment } from '@paintscout/api'
import {
  AppDocsDocument,
  useCompleteTourMutation,
  useMyEngagementQuery,
  useStartTourMutation,
  useUpdateUserMutation
} from '@paintscout/api'
import type { TourConfig, TourEvent, TourStateAdapter } from '@paintscout/ui'
import { TourProvider } from '@paintscout/ui'
import { useUser } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import React, { useCallback, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { track } from 'shared/util/analytics'
import { isE2E } from 'shared/util/e2e'
import { useFlags } from './FlagsProvider'
import {
  isNewTourStart,
  matchingPageViewTourKey,
  nextTourCompletion,
  pickTourRecord,
  SEEN_EVERY_VERSION,
  tourStartTrigger,
  toTourSettings
} from './tourRecords'

/**
 * Gives every product tour in the app its two cross-cutting pieces: completion state, and
 * analytics through `track()`.
 *
 * Mount it once. A new tour is then persisted per user and instrumented because it sits in the
 * tree, not because somebody remembered to wire it up (ENG-1963, ENG-1965).
 *
 * Where completion state lives depends on the `tourManagement` flag (#25347,
 * docs/design/resource-center.md §4):
 * - Off: `preferences.tours` on the user's preferences, read and written as before.
 * - On: the `engagement` table. `read` answers from `myEngagement.tours` (fired by `LoadData`), and
 *   the tour events write it: `start` through `startTour`, `complete`/`dismiss` through
 *   `completeTour`. Events fire for every tour, button-launched ones too, so admin sees every
 *   completion. `write` is then a no-op: `Tour` calls it only for auto-start tours, on the same
 *   close that fires the event. Nothing writes `preferences.tours`.
 *
 * `@paintscout/ui` cannot import from `apps/shared`, so the wiring is injected rather than done in
 * the component. That is why this file exists at all.
 */
export default function AppTourProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const { user, preferences } = useUser()
  const flags = useFlags()
  const tourManagement = flags.tourManagement === true
  const [updateUser] = useUpdateUserMutation()
  const [startTourMutation] = useStartTourMutation()
  const [completeTourMutation] = useCompleteTourMutation()

  // LoadData fires the network request; this only watches the cache, so a completion written by a
  // mutation shows up here without a refetch.
  const { data: engagementData } = useMyEngagementQuery({ fetchPolicy: 'cache-only', skip: !tourManagement })
  const engagementTours = engagementData?.myEngagement?.tours

  /*
    The adapter is handed to context, so it must not change identity on every render — a new object
    each time would restart each tour's auto-start timer. Reads go through refs instead, which
    also means a write is never based on a stale copy of preferences.
  */
  const preferencesRef = useRef(preferences)
  preferencesRef.current = preferences
  const userIdRef = useRef(user?.user_id)
  userIdRef.current = user?.user_id
  const tourManagementRef = useRef(tourManagement)
  tourManagementRef.current = tourManagement
  const toursById = useMemo(
    () => (engagementTours ? new Map(engagementTours.map((tour) => [tour.id, tour])) : null),
    [engagementTours]
  )
  const toursRef = useRef<Map<string, MyTourFieldsFragment> | null>(toursById)
  toursRef.current = toursById
  // Page-view triggers (#25610). Must sit inside the Router.
  const { pathname } = useLocation()
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname
  const pageViewKey = matchingPageViewTourKey(engagementTours, pathname)

  const read = useCallback<TourStateAdapter['read']>((tourId) => {
    const preferenceRecord = preferencesRef.current?.tours?.[tourId] ?? null
    if (!tourManagementRef.current) return preferenceRecord
    // Not loaded: offline with no cached copy. Fail closed, so no tour opens by surprise.
    if (!toursRef.current) return SEEN_EVERY_VERSION
    return pickTourRecord(toursRef.current.get(tourId)?.completion, preferenceRecord)
  }, [])

  const write = useCallback<TourStateAdapter['write']>(
    (tourId, record) => {
      // With tourManagement on, the `complete`/`dismiss` event of this same close writes it.
      if (tourManagementRef.current) return

      const currentPreferences = preferencesRef.current
      const userId = userIdRef.current
      // No user means no preferences document to write to. Nothing to do, and nothing is lost:
      // a tour nobody is signed in for cannot have been "seen" by anyone either.
      if (!userId || !currentPreferences) return

      const nextPreferences = {
        ...currentPreferences,
        tours: { ...(currentPreferences.tours ?? {}), [tourId]: record }
      }

      /*
        Fire-and-forget. The tour has already closed by the time this runs, so a failed write must
        not surface to the user — the only cost is that the tour may auto-start once more.
      */
      void updateUser({
        variables: { input: { user_id: userId, preferences: nextPreferences } },
        // Tour state is worth nothing if it only lands in an offline queue: the next session reads
        // it from appDocs, which comes from the server.
        context: { onlineOnly: true },
        update(cache, { data }) {
          const appDocs = cache.readQuery<AppDocsQuery>({ query: AppDocsDocument })
          if (!appDocs?.appDocs || !data?.updateUser) return

          cache.writeQuery<AppDocsQuery>({
            query: AppDocsDocument,
            data: { ...appDocs, appDocs: { ...appDocs.appDocs, preferences: data.updateUser.preferences } }
          })
        }
      }).catch((error) => {
        console.error('AppTourProvider: failed to save tour state', error)
        Sentry.captureException(error, { tags: { source: 'AppTourProvider', tourId } })
      })
    },
    [updateUser]
  )

  const state = useMemo<TourStateAdapter>(() => ({ read, write }), [read, write])

  // Admin settings (#25348): auto-start on/off and the version, already evaluated for this user by
  // the API. Stable identity for the same reason as the adapter; reads go through the refs. It
  // changes only when the set of page-view tours matching this page changes, so each Tour runs its
  // auto-start check again when the user reaches a page that a tour waits for (#25610).
  const settings = useCallback<NonNullable<TourConfig['settings']>>(
    (tourId) => toTourSettings(!!tourManagementRef.current, toursRef.current?.get(tourId), pathnameRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageViewKey]
  )

  /** Writes the engagement table for a tour event. Fire-and-forget, like `write`. */
  const recordEvent = useCallback(
    (event: TourEvent) => {
      if (!tourManagementRef.current || isE2E()) return
      // Only registered tours have an entry; the server rejects any other id.
      const current = toursRef.current?.get(event.tourId)
      if (!current) return

      const report = (error: unknown) => {
        console.error('AppTourProvider: failed to record tour state', error)
        Sentry.captureException(error, { tags: { source: 'AppTourProvider', tourId: event.tourId } })
      }
      // Same reason as `write`: an offline queue would replay this into a later session.
      const context = { onlineOnly: true }

      if (event.name === 'start') {
        if (!isNewTourStart(current.startedVersion, event.version)) return
        void startTourMutation({
          variables: { id: event.tourId, version: event.version },
          context,
          optimisticResponse: { startTour: { ...current, startedVersion: event.version } }
        }).catch(report)
        return
      }

      if (event.name !== 'complete' && event.name !== 'dismiss') return
      const dismissed = event.name === 'dismiss'
      const next = nextTourCompletion(current.completion, {
        version: event.version,
        dismissed,
        completedAt: new Date().toISOString()
      })
      // The server would record nothing: a replay of a version already finished.
      if (!next) return
      void completeTourMutation({
        variables: { id: event.tourId, version: event.version, dismissed },
        context,
        // The ref, and so `read`, sees the new record at once, so a replay in this session agrees.
        optimisticResponse: {
          completeTour: { ...current, completion: { __typename: 'MyTourCompletion', ...next } }
        }
      }).catch(report)
    },
    [startTourMutation, completeTourMutation]
  )

  const onEvent = useCallback(
    (event: TourEvent) => {
      const { name, autoStarted, ...properties } = event
      // One event name per lifecycle point, so PostHog can build a funnel without a property filter.
      track(
        `tour:${name}`,
        name === 'start'
          ? { ...properties, trigger: tourStartTrigger(autoStarted, toursRef.current?.get(event.tourId)) }
          : properties
      )
      recordEvent(event)
    },
    [recordEvent]
  )

  return (
    <TourProvider state={state} onEvent={onEvent} settings={settings}>
      {children}
    </TourProvider>
  )
}
